import { useTranslation } from "react-i18next";
import Dialog from "../../../components/Dialog";
import SubmitButton from "../../../components/SubmitButton";
import { useConfirmationWithIntl } from "../../../components/ConfirmationDialog";
import useReload from "../../../hooks/useReload";
import { Organization } from "../../../services/organization/organizationModel";
import {
  PREMIUM_SPONSORSHIP_STATUS_LABEL_KEYS,
  PremiumSponsorship,
  PremiumSponsorshipStatus,
} from "../../../services/premiumSponsorship/premiumSponsorshipModel";
import { premiumSponsorshipService } from "../../../services/premiumSponsorship/premiumSponsorshipService";
import { toastsWithIntl } from "../../../services/toastService";

interface Props {
  onClose(): void;
  organization: Organization;
  sponsorship: PremiumSponsorship;
}

// Couleur de la puce selon le statut (maquette : chipStatus). Partagée avec le tableau pour que la liste
// et la fenêtre de gestion montrent le même statut de la même façon.
const STATUS_CHIP_MODIFIERS: Record<PremiumSponsorshipStatus, string> = {
  invited: "--warn",
  accepted: "--ok",
  declined: "--err",
  revoked: "--off",
};

export const SponsorshipStatusChip = ({
  status,
}: {
  status: PremiumSponsorshipStatus;
}) => {
  const { t } = useTranslation(["organization"]);
  return (
    <span className={`ui-chip ${STATUS_CHIP_MODIFIERS[status]}`}>
      {t(PREMIUM_SPONSORSHIP_STATUS_LABEL_KEYS[status])}
    </span>
  );
};

const {
  resendPremiumSponsorshipInvitation,
  revokePremiumSponsorship,
  deletePremiumSponsorship,
} = premiumSponsorshipService();

const ManagePremiumSponsorshipDialog = ({
  onClose,
  organization,
  sponsorship,
}: Props) => {
  const { t } = useTranslation(["organization"]);
  const { toastSuccess } = toastsWithIntl(["organization"]);
  const { confirm } = useConfirmationWithIntl(["organization"]);
  const reload = useReload();

  return (
    <Dialog onClose={onClose} className="sp-dialog">
      <>
        <div className="popup_head">
          <div className="popup_title">
            {t("organization:premium-sponsorship.manage.TITLE")}
          </div>
        </div>
        <div className="popup_body">
          <p className="sp-manage_who">
            <b>{sponsorship.email}</b>
            <SponsorshipStatusChip status={sponsorship.status} />
          </p>

          <div className="sp-manage_actions">
            {sponsorship.status === "invited" && (
              <>
                <SubmitButton
                  className="btn--soft"
                  type="button"
                  onClick={() => {
                    return resendPremiumSponsorshipInvitation(
                      organization.id,
                      sponsorship.id,
                    );
                  }}
                >
                  {t(
                    "organization:premium-sponsorship.manage.RESEND_INVITATION",
                  )}
                </SubmitButton>
                {sponsorship.token && (
                  <button
                    className="btn--soft"
                    type="button"
                    onClick={(ev) => {
                      if (ev.isTrusted)
                        void navigator.clipboard
                          .writeText(
                            `${location.origin}/premium-sponsorship/${sponsorship.token}`,
                          )
                          .then(() => {
                            toastSuccess(
                              "organization:premium-sponsorship.manage.LINK_COPIED",
                              {
                                id: "sponsorship-invitation-link",
                              },
                            );
                          });
                    }}
                  >
                    {t(
                      "organization:premium-sponsorship.manage.COPY_INVITATION_LINK",
                    )}
                  </button>
                )}
              </>
            )}
            {(sponsorship.status === "invited" ||
              sponsorship.status === "accepted") && (
              <button
                className="btn--soft --danger"
                type="button"
                onClick={() => {
                  confirm(
                    "organization:premium-sponsorship.confirm-revoke.MESSAGE",
                    () =>
                      revokePremiumSponsorship(
                        organization.id,
                        sponsorship.id,
                      ).then(() => {
                        reload();
                        onClose();
                      }),
                    true,
                  );
                }}
              >
                {t("organization:premium-sponsorship.manage.REVOKE")}
              </button>
            )}
          </div>

          <div className="form_footer --txt--right">
            <button
              className="btn"
              type="button"
              onClick={() => {
                confirm(
                  "organization:premium-sponsorship.confirm-delete.MESSAGE",
                  () =>
                    deletePremiumSponsorship(
                      organization.id,
                      sponsorship.id,
                    ).then(() => {
                      reload();
                      onClose();
                    }),
                  true,
                  {
                    email: sponsorship.email,
                  },
                );
              }}
            >
              {t("organization:premium-sponsorship.manage.DELETE")}
            </button>
          </div>
        </div>
      </>
    </Dialog>
  );
};

export default ManagePremiumSponsorshipDialog;
